import React, { useEffect, useMemo, useState } from 'react';
import { IAppContextProp, withAppContext } from '../../../../common/contexts/AppContext';
import { ensureProjectOfType, ILocationProp, INavigateProp, withLocationAndNavigate, withParams } from '../../../RouterHelper';
import { ContentCloudPageLayout } from '../Layouts/ContentCloudPageLayout';
import { AssetEntry, ExternalEntryLinkType, buildImageUrl, ContentCloudSystemRestClient } from '../ContentCloudSystemRestClient';
import { ContentCloudComponentProps, withContentCloud } from '../WithContentCloud';
import { CONTENT_CLOUD_API_VERSION, getContentCloudSatelliteUrl, getFileSizeDisplay, getNumberDisplay } from '../content-cloud-helper';
import { ContentCloudPermission } from '../shared-permissions';
import { CopyToClipboardButton, IconButton, LeftRightContainer, LoadingBar, Alert, Image, Tab, Tabs } from '@edgebox/react-components';
import { HeadlineWithBreadcrumbNavigation } from '../../../../common/components/BreadcrumbNavigation';
import { EntryPublishedBadge } from '../Components/EntryPublishedBadge';
import { EntryRelativeDate } from '../Components/EntryRelativeDate';
import { Link } from 'react-router-dom';
import { ContentCloudDetailsPageLayout } from '../Layouts/ContentCloudDetailsPageLayout';
import { ContentEntryPropertyWrapper, StringPropertyValue } from './Components/ContentEntryProperties';
import { faDownload } from '@fortawesome/pro-light-svg-icons/faDownload';
import { SiteApplicationType } from '@edgebox/sync-core-data-definitions';

interface Props extends React.JSX.IntrinsicAttributes, IAppContextProp, INavigateProp, ILocationProp, ContentCloudComponentProps {
  params: {
    project: string;
    asset: string;
    tab: string;
  };
}

function AssetEntryPageComponent({ contentCloudData, params, location, navigate }: Props) {
  const { accessToken, contentCloud, environment, space } = contentCloudData ?? {};

  const client = useMemo(
    () =>
      contentCloud &&
      accessToken &&
      environment &&
      space &&
      new ContentCloudSystemRestClient({
        accessToken,
        baseUrl: getContentCloudSatelliteUrl(contentCloud, {
          api: 'rest',
          environmentSubdomain: `${space.domainKey}-${environment.domainKey}`,
          service: 'live',
          version: CONTENT_CLOUD_API_VERSION,
        }),
      }),
    [accessToken, space, environment, contentCloud]
  );

  const [sidebarTab, setSidebarTab] = useState('general');
  const [contentTab, _setContentTab] = useState(params.tab);
  const setContentTab = useMemo(
    () => (tab: string) => {
      _setContentTab(tab);
      navigate(`/projects/${params.project}/content-cloud/assets/${params.asset}/${tab}${location.search}`);
    },
    [navigate, _setContentTab, params, location]
  );

  const search = new URLSearchParams(location.search);
  const locale = search.get('locale');
  const [assetEntry, setAssetEntry] = useState<AssetEntry | null>(null);
  useEffect(() => {
    setAssetEntry(null);

    client && client.assetEntry({ id: params.asset, ...(locale ? { locale } : {}) }).then((AssetEntry) => setAssetEntry(AssetEntry));
  }, [client, params.asset, locale]);

  const json = useMemo(() => JSON.stringify(assetEntry, null, 2), [assetEntry]);

  const source = assetEntry?.externalLinks?.find((c) => c.linkType === ExternalEntryLinkType.Source);

  return (
    <ContentCloudPageLayout>
      <ContentCloudDetailsPageLayout
        pageHeader={
          <HeadlineWithBreadcrumbNavigation className="ms-2" names={{ [params.asset]: assetEntry?.fields.title ?? '...' }}>
            {assetEntry?.sys.name ?? '...'}
          </HeadlineWithBreadcrumbNavigation>
        }
        sidebar={
          <Tabs id={'sidebar-tabs'} className={'mb-3'} activeKey={sidebarTab} onSelect={(k: string | number) => setSidebarTab(String(k))}>
            <Tab eventKey={'general'} title={'General'}>
              <div>
                <div className="mb-4">
                  <div className="small fw-bold border-bottom text-muted pb-1">Status</div>
                  <LeftRightContainer
                    className="pt-2"
                    left={<span className="text-muted">Current</span>}
                    right={assetEntry ? <EntryPublishedBadge sys={assetEntry.sys} /> : null}
                  />
                </div>

                <div className="mb-4">
                  <div className="small fw-bold border-bottom text-muted pb-1">Publication</div>
                  {assetEntry && (
                    <LeftRightContainer
                      className="pt-2"
                      left={assetEntry.sys.isPublished ? 'Published at' : 'Draft saved at'}
                      right={<EntryRelativeDate date={assetEntry.sys.updatedAt} />}
                    />
                  )}
                  {assetEntry && assetEntry.sys.createdAt && assetEntry.sys.createdAt !== assetEntry.sys.updatedAt && (
                    <LeftRightContainer
                      className="pt-2"
                      left={assetEntry.sys.isPublished ? 'First published at' : 'Created at'}
                      right={<EntryRelativeDate date={assetEntry.sys.createdAt} />}
                    />
                  )}
                </div>
              </div>
            </Tab>

            <Tab eventKey={'source'} title={'Source'}>
              <div>
                {source ? (
                  <div className="mb-4">
                    <div className="small fw-bold border-bottom text-muted pb-1">Canonical URL</div>
                    {source.canonicalUrl ? (
                      <Link to={source.canonicalUrl} target="_blank" className="text-truncate d-block">
                        {source.domain}
                      </Link>
                    ) : (
                      <em>Not provided.</em>
                    )}
                  </div>
                ) : (
                  <Alert variant="light">This entry doesn't include any source information.</Alert>
                )}
              </div>
            </Tab>
            {/*<Tab eventKey={'links'} title={'Links'}>
                    <div>Links</div>
                  </Tab>*/}
            <Tab eventKey={'info'} title={'Info'}>
              <div>
                <div className="mb-4">
                  <div className="small fw-bold border-bottom text-muted pb-1">Asset</div>
                  <LeftRightContainer
                    className="pt-2"
                    left={'File name'}
                    right={<div className="text-truncate">{assetEntry?.fields.file.fileName}</div>}
                  />
                  <LeftRightContainer
                    className="pt-2"
                    left={'Type'}
                    right={<div className="text-truncate">{assetEntry?.fields.file.mimeTypeGroup}</div>}
                  />
                  <LeftRightContainer
                    className="pt-2"
                    left={'File size'}
                    right={
                      <div className="text-truncate">
                        {assetEntry && assetEntry.fields.file.details && getFileSizeDisplay(assetEntry.fields.file.details.size)}
                      </div>
                    }
                  />
                  {assetEntry && assetEntry.fields.file.details?.image && (
                    <LeftRightContainer
                      className="pt-2"
                      left={'Width / Height'}
                      right={
                        <div className="text-truncate">
                          {getNumberDisplay(assetEntry.fields.file.details.image.width!)} x{' '}
                          {getNumberDisplay(assetEntry.fields.file.details.image.height!)}
                        </div>
                      }
                    />
                  )}
                </div>

                <div className="mb-4">
                  <div className="small fw-bold border-bottom text-muted pb-1">Entry</div>
                  <LeftRightContainer
                    className="pt-2"
                    left={<span>ID</span>}
                    right={
                      <span>
                        {assetEntry?.sys.id}{' '}
                        <CopyToClipboardButton name="copy-entry-id" text={assetEntry?.sys.id ?? '...'} buttonText="" buttonOnly />
                      </span>
                    }
                  />
                </div>

                <div className="mb-4">
                  <div className="small fw-bold border-bottom text-muted pb-1">Version</div>
                  <LeftRightContainer className="pt-2" left={<span>Entry</span>} right={<span>{assetEntry?.sys.revision}</span>} />
                  <LeftRightContainer
                    className="pt-2"
                    left={<span>Version ID</span>}
                    right={
                      <CopyToClipboardButton
                        name="copy-version-id"
                        text={assetEntry?.sys.versionId ?? '...'}
                        buttonText=" Copy"
                        buttonOnly
                      />
                    }
                  />
                  <LeftRightContainer
                    className="pt-2"
                    left={<span>File hash</span>}
                    right={
                      <CopyToClipboardButton
                        name="copy-file-hash"
                        text={assetEntry?.fields.file.hash ?? '...'}
                        buttonText=" Copy"
                        buttonOnly
                      />
                    }
                  />
                </div>
              </div>
            </Tab>
          </Tabs>
        }
      >
        <Tabs id={'content-tabs'} className={'mb-3'} activeKey={contentTab} onSelect={(k: string | number) => setContentTab(String(k))}>
          <Tab eventKey={'content'} title={'Content'}>
            <ContentEntryPropertyWrapper label="Title">
              {assetEntry ? <StringPropertyValue>{assetEntry.fields.title}</StringPropertyValue> : <LoadingBar />}
            </ContentEntryPropertyWrapper>

            {assetEntry && assetEntry.fields.description && (
              <ContentEntryPropertyWrapper label="Description">
                <StringPropertyValue>{assetEntry.fields.description}</StringPropertyValue>
              </ContentEntryPropertyWrapper>
            )}

            <ContentEntryPropertyWrapper label="File">
              {assetEntry && assetEntry.fields.file.embedUrl ? (
                assetEntry.fields.file.imageUrl ? (
                  <div>
                    <Image
                      src={buildImageUrl(assetEntry.fields.file.imageUrl, { height: 300, fit: 'scale-down', format: 'webp' })}
                      height={300}
                    />
                  </div>
                ) : (
                  <div>
                    <embed src={assetEntry.fields.file.embedUrl} width="100%" height="300" type={assetEntry.fields.file.contentType} />
                  </div>
                )
              ) : null}
              {assetEntry && assetEntry.fields.file.downloadUrl && (
                <div className="mt-2">
                  <IconButton to={assetEntry.fields.file.downloadUrl} target="_blank" variant="light" icon={faDownload}>
                    Download
                  </IconButton>
                </div>
              )}
            </ContentEntryPropertyWrapper>
          </Tab>
          {/*<Tab eventKey={'links'} title={'Links'}>
                    Links
                  </Tab>
                  <Tab eventKey={'tags'} title={'Tags'}>
                    Tags
                  </Tab>
                  <Tab eventKey={'media'} title={'Media'}>
                    Media
                  </Tab>*/}
          <Tab eventKey={'json'} title={'JSON Preview'}>
            <code className="border rounded border-light p-2 d-block mx-2 mb-2 position-relative">
              <div className="position-absolute" style={{ top: '10px', right: '10px' }}>
                <CopyToClipboardButton name="copy-json" buttonText=" Copy" text={json} buttonOnly />
              </div>
              <pre>{json}</pre>
            </code>
          </Tab>
        </Tabs>
      </ContentCloudDetailsPageLayout>
    </ContentCloudPageLayout>
  );
}

export const AssetEntryPage = ensureProjectOfType(
  withParams<{}, { site: string; tab: string }>(
    withLocationAndNavigate<any>(
      withAppContext<any>(
        withContentCloud(AssetEntryPageComponent, [
          ContentCloudPermission.CONTENT_READ,
          ContentCloudPermission.SPACE_READ,
          ContentCloudPermission.PREVIEW,
          ContentCloudPermission.ASSET_READ_FILE,
          ContentCloudPermission.EXTERNAL_LINK_READ,
        ])
      )
    )
  ),
  SiteApplicationType.ContentCloud
);
